import React, { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function PaymentSuccess() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const sessionId = sp.get("session_id");
  const [status, setStatus] = useState({ payment_status: "pending" });
  const [tries, setTries] = useState(0);

  useEffect(() => {
    if (!sessionId) return;
    let stop = false;
    const poll = async () => {
      if (stop) return;
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        setStatus(data);
        if (data.payment_status === "paid" || tries >= 20) return;
      } catch (_) {}
      setTimeout(() => setTries((t) => t + 1), 2000);
    };
    poll();
    return () => { stop = true; };
  }, [sessionId, tries]);

  const paid = status.payment_status === "paid";
  const failed = ["failed", "expired", "canceled"].includes(status.payment_status);

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#FAFAF7] px-6 text-slate-900">
      <div className="w-full max-w-md border border-slate-200 bg-white p-10 text-center">
        {paid ? (
          <>
            <CheckCircle2 className="mx-auto mb-4 h-12 w-12 text-emerald-400" />
            <h1 className="font-serif text-3xl font-semibold">You're on {status.plan?.toUpperCase()}.</h1>
            <p className="mt-3 text-sm text-slate-500">
              Unlimited projects, saved sets, and all Pro features are unlocked. A receipt is on its way to your inbox.
            </p>
            <button
              data-testid="payment-success-continue"
              onClick={() => nav("/workspace")}
              className="mt-8 inline-flex items-center gap-2 bg-[#2563EB] px-5 py-3 text-sm font-semibold text-white hover:bg-[#1D4ED8]"
            >
              Take me to the workspace
            </button>
          </>
        ) : failed ? (
          <>
            <XCircle className="mx-auto mb-4 h-12 w-12 text-rose-400" />
            <h1 className="font-serif text-3xl font-semibold">Payment didn't go through.</h1>
            <p className="mt-3 text-sm text-slate-500">{status.payment_status}. You can retry anytime.</p>
            <Link
              to="/pricing"
              className="mt-8 inline-flex items-center gap-2 border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-900 hover:border-[#2563EB]"
            >
              Back to pricing
            </Link>
          </>
        ) : (
          <>
            <Loader2 className="mx-auto mb-4 h-12 w-12 animate-spin text-slate-500" />
            <h1 className="font-serif text-3xl font-semibold">Confirming payment…</h1>
            <p className="mt-3 text-sm text-slate-500">This takes a couple of seconds.</p>
          </>
        )}
      </div>
    </div>
  );
}
